import React from 'react';
import { useHealth, useStatus, useAPIStatusSummary } from '@/hooks/use-api';
import { APIUtils } from '@/utils/api-utils';
import {
  Activity,
  CheckCircle,
  XCircle,
  AlertTriangle,
  RefreshCw,
  Clock,
  Zap,
  Server
} from 'lucide-react';
import toast from 'react-hot-toast';

function HealthStatus() {
  const { data: health, loading: healthLoading, error: healthError, refetch: refetchHealth } = useHealth();
  const { data: status, loading: statusLoading, error: statusError } = useStatus();
  const { summary, loading: summaryLoading, refetch: refetchSummary } = useAPIStatusSummary();

  const handleRefresh = async () => {
    toast.promise(
      Promise.all([refetchHealth(), refetchSummary()]),
      {
        loading: 'Refreshing health status...',
        success: 'Health status updated',
        error: 'Failed to refresh health status',
      }
    );
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'healthy':
      case 'operational':
        return <CheckCircle className="w-6 h-6 text-green-600" />;
      case 'unhealthy':
      case 'down':
        return <XCircle className="w-6 h-6 text-red-600" />;
      case 'degraded':
        return <AlertTriangle className="w-6 h-6 text-yellow-600" />;
      default:
        return <Activity className="w-6 h-6 text-gray-600" />;
    }
  };

  const isLoading = healthLoading || statusLoading || summaryLoading;
  const hasError = healthError || statusError;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">System Health</h1>
          <p className="text-gray-600 mt-2">
            Monitor the health and status of the UDA MCP Server
          </p>
        </div>
        <button
          onClick={handleRefresh}
          disabled={isLoading}
          className="flex items-center space-x-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
          <span>Refresh</span>
        </button>
      </div>

      {/* Error State */}
      {hasError && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center space-x-3">
            <XCircle className="w-5 h-5 text-red-600" />
            <div>
              <h3 className="text-sm font-medium text-red-800">Connection Error</h3>
              <p className="text-sm text-red-700 mt-1">
                Unable to connect to the API server. This may be due to DNS propagation or network issues.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Health Overview Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Health Card */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">Health Check</h3>
            {isLoading ? (
              <RefreshCw className="w-5 h-5 text-gray-400 animate-spin" />
            ) : (
              getStatusIcon(health?.status || 'unknown')
            )}
          </div>
          <div className="space-y-2">
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Status:</span>
              <span className={`px-2 py-1 text-xs font-semibold rounded-full ${APIUtils.getHealthColor(health?.status || 'unknown')}`}>
                {health?.status || 'Unknown'}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Version:</span>
              <span className="text-sm font-medium">{health?.version || 'N/A'}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Environment:</span>
              <span className="text-sm font-medium">{health?.environment || 'N/A'}</span>
            </div>
            {health?.timestamp && (
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">Last Check:</span>
                <span className="text-sm font-medium">
                  {APIUtils.formatTimestamp(health.timestamp)}
                </span>
              </div>
            )}
          </div>
        </div>

        {/* Service Status Card */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">Service Status</h3>
            <Server className="w-5 h-5 text-gray-400" />
          </div>
          <div className="space-y-2">
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Service:</span>
              <span className="text-sm font-medium">{status?.service || 'N/A'}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Status:</span>
              <span className={`px-2 py-1 text-xs font-semibold rounded-full ${APIUtils.getHealthColor(status?.status || 'unknown')}`}>
                {status?.status || 'Unknown'}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Endpoints:</span>
              <span className="text-sm font-medium">{status?.endpoints?.length || 0} available</span>
            </div>
          </div>
        </div>

        {/* API Summary Card */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">API Summary</h3>
            <Zap className="w-5 h-5 text-gray-400" />
          </div>
          <div className="space-y-2">
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Available:</span>
              <span className={`px-2 py-1 text-xs font-semibold rounded-full ${
                summary?.available ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
              }`}>
                {summary?.available ? 'Online' : 'Offline'}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Errors:</span>
              <span className="text-sm font-medium">{summary?.errors?.length || 0}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Uptime:</span>
              <span className="text-sm font-medium">
                {summary?.metrics?.uptime ? APIUtils.formatResponseTime(summary.metrics.uptime) : 'N/A'}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Detailed Information */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Service Endpoints */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Available Endpoints</h3>
          {status?.endpoints ? (
            <div className="space-y-2">
              {status.endpoints.map((endpoint: string, index: number) => (
                <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <code className="text-sm font-mono text-gray-700">{endpoint}</code>
                  <CheckCircle className="w-4 h-4 text-green-600" />
                </div>
              ))}
            </div>
          ) : (
            <p className="text-gray-500">No endpoint information available</p>
          )}
        </div>

        {/* Errors and Issues */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">System Issues</h3>
          {summary?.errors && summary.errors.length > 0 ? (
            <div className="space-y-2">
              {summary.errors.map((error: string, index: number) => (
                <div key={index} className="flex items-start space-x-2 p-3 bg-red-50 rounded-lg">
                  <AlertTriangle className="w-4 h-4 text-red-600 mt-0.5 flex-shrink-0" />
                  <span className="text-sm text-red-800">{error}</span>
                </div>
              ))}
            </div>
          ) : (
            <div className="flex items-center space-x-2 p-3 bg-green-50 rounded-lg">
              <CheckCircle className="w-4 h-4 text-green-600" />
              <span className="text-sm text-green-800">No issues detected</span>
            </div>
          )}
        </div>
      </div>

      {/* Quick Actions */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <button
            onClick={() => window.open('https://api.fastpm.dev/health', '_blank')}
            className="flex items-center justify-center space-x-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
          >
            <Activity className="w-4 h-4" />
            <span>Check Health API</span>
          </button>
          <button
            onClick={() => window.open('https://api.fastpm.dev/metrics', '_blank')}
            className="flex items-center justify-center space-x-2 px-4 py-2 bg-secondary-600 text-white rounded-lg hover:bg-secondary-700 transition-colors"
          >
            <Zap className="w-4 h-4" />
            <span>View Metrics</span>
          </button>
          <button
            onClick={handleRefresh}
            disabled={isLoading}
            className="flex items-center justify-center space-x-2 px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
            <span>Refresh Data</span>
          </button>
        </div>
      </div>
    </div>
  );
}

export default HealthStatus;